import React, { useEffect, useContext } from 'react';
import type { ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Routes,
  Route,
  useLocation,
  Navigate,
} from 'react-router';
import { Helmet } from 'react-helmet-async';
import * as Sentry from '@sentry/react';

import { useGetAppConfigForSiteQuery } from '#services/app';
import { useCheckMutation } from '#services/auth';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import Login from '#features/auth/Login';
import { setUserContext } from '#utils/errorReporting';
import i18n, { SUPPORTED_LANGUAGES, getStoredLocale } from '#i18n';
import { setLocale } from '#features/app/config.slice';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

interface AppConfigState {
  ready?: boolean;
  loading?: boolean;
  site?: string;
  wholesaleName?: string;
  locale?: string;
  googleAnalytics?: string;
  [key: string]: unknown;
}

interface AppProps {
  bootstrap: { composeRoute: () => ReactNode };
}

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (process.env.SENTRY_DSN) {
      Sentry.captureException(error, {
        extra: { ...errorInfo },
      });
    } else {
      console.error('Caught an error:', error);
    }
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }

    return this.props.children;
  }
}

export default function App(props: AppProps) {
  const { bootstrap } = props;

  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: { auth: { isLoggedIn: boolean } }) => state.auth.isLoggedIn);
  const user = useSelector((state: { auth: { user?: unknown } }) => state.auth.user);
  const appConfig = useSelector((state: { appConfig: AppConfigState }) => state.appConfig);
  const location = useLocation();
  const [doCheck] = useCheckMutation();
  const {
    showBackdrop,
    hideBackdrop,
    hideModal,
  } = useContext(FeedbackUIContext)!;

  useGetAppConfigForSiteQuery(undefined);

  useEffect(() => {
    // Set up Sentry user context when user logs in/out, only if Sentry is initialized
    if (process.env.SENTRY_DSN) {
      setUserContext((isLoggedIn ? user : null) as Parameters<typeof setUserContext>[0]);

      // Set application context for better error reporting
      if (appConfig.ready && appConfig.site) {
        Sentry.setTag('site', appConfig.site);
        Sentry.setTag('wholesale_name', appConfig.wholesaleName);
      }
    }
  }, [isLoggedIn, user, appConfig.ready, appConfig.site]);

  useEffect(() => {
    void doCheck(undefined);
    setInterval(() => {
      if (window.location.href.includes('/login')) {
        return;
      }
      if (document.hidden) {
        return;
      }
      void doCheck(undefined);
    }, 1000 * 60 * 5);

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        void doCheck(undefined);
      }
    });
  }, []);

  useEffect(() => {
    // close modal on route change
    hideModal();
  }, [location.key]);

  useEffect(() => {
    if (appConfig.loading) {
      showBackdrop();
    } else {
      hideBackdrop();
    }
  }, [appConfig.loading]);

  // Sync i18n language when appConfig arrives.
  // Resolution: explicit localStorage choice > appConfig.locale > boot default.
  useEffect(() => {
    if (!appConfig.ready) return;
    const userChosen = getStoredLocale();
    const configLocale = SUPPORTED_LANGUAGES.includes(appConfig.locale as string) ? appConfig.locale : null;
    const targetLocale = (userChosen && SUPPORTED_LANGUAGES.includes(userChosen))
      ? userChosen
      : (configLocale || i18n.language);
    if (i18n.language !== targetLocale) {
      void i18n.changeLanguage(targetLocale);
    }
    dispatch(setLocale(targetLocale));
  }, [appConfig.ready, appConfig.locale]);

  useEffect(() => {
    if (appConfig.googleAnalytics) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${appConfig.googleAnalytics}`;
      script.async = true;
      document.head.appendChild(script);

      script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        // gtag.js only processes `Arguments` objects pushed to dataLayer —
        // a real array (from rest params) is silently ignored, disabling tracking.
        // eslint-disable-next-line prefer-rest-params
        function gtag(..._args: unknown[]) { window.dataLayer?.push(arguments); }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', appConfig.googleAnalytics);
      };

      // Cleanup function to remove the script when component unmounts or updates
      return () => {
        document.head.removeChild(script);
      };
    }
    return undefined;
  }, [appConfig.googleAnalytics]);

  if (!appConfig.ready) {
    return null;
  }

  let loginElement = <Login />;
  if (isLoggedIn) {
    const state = (location.state || {}) as { from?: { pathname?: string; search?: string } };
    const target = (state.from && state.from.pathname)
      ? `${state.from.pathname}?${state.from.search}`
      : '/';
    loginElement = <Navigate to={target} replace />;
  }

  const subTitle = location?.pathname.split('/').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

  return (
    <ErrorBoundary>
      <Helmet>
        <title>{`${subTitle.trim().length > 0 ? subTitle : 'Home'} | ${appConfig.wholesaleName ?? ''}`}</title>
      </Helmet>
      <Routes>
        <Route path="/login" element={loginElement} />
        {bootstrap.composeRoute()}
      </Routes>
    </ErrorBoundary>
  );
}
